function _classCallCheck(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _defineProperties(target, props) {
    for(var i = 0; i < props.length; i++){
        var descriptor = props[i];
        descriptor.enumerable = descriptor.enumerable || false;
        descriptor.configurable = true;
        if ("value" in descriptor) descriptor.writable = true;
        Object.defineProperty(target, descriptor.key, descriptor);
    }
}
function _createClass(Constructor, protoProps, staticProps) {
    if (protoProps) _defineProperties(Constructor.prototype, protoProps);
    if (staticProps) _defineProperties(Constructor, staticProps);
    return Constructor;
}
/**
 * featureFlags.ts
 *
 * This file maps client feature flags for the front-end.
 *
 * Feature flags can be opted in for client use by mapping them to
 * an enum value in ClientFeatureFlag.cs. The feature flag export here
 * should check if the clientFeatureFlags array includes the exact name
 * of the middle-tier ClientFeatureFlag enum value (PascalCase).
 *
 * These feature flags are transmitted to the client via JWT claims cookie
 * (ff claim). The cookies token value is base64 decoded on the client to
 * attain the feature flags synchronously.
 *
 * When running development build feature flags state can be queried via
 * chrome developer console by executing `featureFlags` (global).
 */ import { getSessionClaims } from "./sessionClaims";
// (b/153007582): This will only work for routes receiving the JWT.
// When the feature above is implemented, this can be used to detect
// ClientFeatureFlag across Kaggle.
var flagsCache;
var getFeatureFlags = function() {
    if (!flagsCache) {
        var ref;
        flagsCache = ((ref = getSessionClaims()) === null || ref === void 0 ? void 0 : ref.ff) || [];
    }
    return flagsCache;
};
var flagDetailsCache;
var getFeatureFlagsDetails = function() {
    if (!flagDetailsCache) {
        var ref;
        flagDetailsCache = ((ref = getSessionClaims()) === null || ref === void 0 ? void 0 : ref.ffd) || {};
    }
    return flagDetailsCache;
};
var getFeatureFlagValue = function(flagName) {
    return getFeatureFlagsDetails()[flagName];
};
var FeatureFlags = // Using 'class' for getters.
/*#__PURE__*/ function() {
    "use strict";
    function FeatureFlags() {
        _classCallCheck(this, FeatureFlags);
    }
    _createClass(FeatureFlags, [
        {
            key: "gcloudKernelInteg",
            get: /** The gcloud-kernel-ui FF. */ function get() {
                return getFeatureFlags().includes("GcloudKernelInteg");
            }
        },
        {
            key: "caipExport",
            get: /** The caip-export-ui FF. */ function get() {
                return getFeatureFlags().includes("CaipExport");
            }
        },
        {
            key: "caipNudge",
            get: /** The caip-nudge-ui FF. */ function get() {
                return getFeatureFlags().includes("CaipNudge");
            }
        },
        {
            key: "dockerModalSelector",
            get: /** Shows the new Docker Modal selector */ function get() {
                return getFeatureFlags().includes("DockerModalSelector");
            }
        },
        {
            key: "forceSiteWideDarkTheme",
            get: /**
   * Forces dark theme to be enabled across the site. Not intended to be
   * launched, just useful to see the state of dark mode adoption. b/202752236
   */ function get() {
                return getFeatureFlags().includes("ForceSiteWideDarkTheme");
            }
        },
        {
            key: "kernelsFirebaseProxy",
            get: /** The kernels-firebase-proxy FF. */ function get() {
                return getFeatureFlags().includes("KernelsFirebaseProxy");
            }
        },
        {
            key: "kernelsGCSUploadProxy",
            get: /** The kernels-gcs-upload-proxy FF. */ function get() {
                return getFeatureFlags().includes("KernelsGCSUploadProxy");
            }
        },
        {
            key: "kernelsFirebaseLongPolling",
            get: /** The kernels-firebase-long-polling FF. */ function get() {
                return getFeatureFlags().includes("KernelsFirebaseLongPolling");
            }
        },
        {
            key: "kernelsFirestoreDebugLogging",
            get: /** The kernels-firestore-debug-logging FF. */ function get() {
                return getFeatureFlags().includes("KernelsFirestoreDebugLogging");
            }
        },
        {
            key: "kernelsSaveToGitHub",
            get: /** The kernels-save-to-github FF. */ function get() {
                return getFeatureFlags().includes("KernelsSaveToGitHub");
            }
        },
        {
            key: "kernelsResume",
            get: function get() {
                return getFeatureFlags().includes("KernelsResume");
            }
        },
        {
            key: "kernelsLspAutocomplete",
            get: function get() {
                return getFeatureFlags().includes("KernelsLspAutocomplete");
            }
        },
        {
            key: "kernelsDraftUploadBlob",
            get: function get() {
                return getFeatureFlags().includes("KernelsDraftUploadBlob");
            }
        },
        {
            key: "kernelEditorAutosaveThrottleMs",
            get: function get() {
                return getFeatureFlagValue("KernelEditorAutosaveThrottleMs");
            }
        },
        {
            key: "kernelEditorForceThemeSync",
            get: /**
   * When enabled, poll JL frame for the active theme and force the theme to
   * sync if needed. http://b/216866217
   */ function get() {
                return getFeatureFlags().includes("KernelEditorForceThemeSync");
            }
        },
        {
            key: "kernelEditorKittyMode",
            get: function get() {
                return getFeatureFlags().includes("KernelEditorKittyMode");
            }
        },
        {
            key: "datasetsPrependTopicalDatasetsInPublicList",
            get: /** Show the topical dataset V1 FF. */ function get() {
                return getFeatureFlags().includes("DatasetsPrependTopicalDatasetsInPublicList");
            }
        },
        {
            key: "avatarProfilePreview",
            get: function get() {
                return getFeatureFlags().includes("AvatarProfilePreview");
            }
        },
        {
            key: "kernelsSnippetEditor",
            get: function get() {
                return getFeatureFlags().includes("KernelsSnippetEditor");
            }
        },
        {
            key: "datasetsDataExplorerV3CheckForUpdates",
            get: /** Enable "Check for updates" menu item in DataExplorerV3 in Notebook editor. */ function get() {
                return getFeatureFlags().includes("DatasetsDataExplorerV3CheckForUpdates");
            }
        },
        {
            key: "datasetsDataExplorerV3CheckForUpdatesInBackground",
            get: /** Enable automatic dataset update check feature in DataExplorer in Notebook editor. */ function get() {
                return getFeatureFlags().includes("DatasetsDataExplorerV3CheckForUpdatesInBackground");
            }
        },
        {
            key: "kernelsStackOverflowSearch",
            get: /** Surfaces a button in kernel errors to search stack overflow. */ function get() {
                return getFeatureFlags().includes("KernelsStackOverflowSearch");
            }
        },
        {
            key: "kernelEditorScriptTips",
            get: /** Show tips on common coding errors in the editor. */ function get() {
                return getFeatureFlags().includes("KernelEditorScriptTips");
            }
        },
        {
            key: "kernelsMaterialListing",
            get: /** Uses the new Material Listing for the /notebooks page. */ function get() {
                return getFeatureFlags().includes("KernelsMaterialListing");
            }
        },
        {
            key: "kernelsEmptyState",
            get: /** Whether to use KM EmptyState instead of EmptyListPlaceholder in the MaterialKernelsList component */ function get() {
                return getFeatureFlags().includes("KernelsEmptyState");
            }
        },
        {
            key: "kernelViewerRequestSourceFromFrontend",
            get: /**
   * Requests source (KernelBlob.source) from the frontend in edge cases when
   * it is needed, instead of on every GetKernelViewModelRequest. b/189875518
   */ function get() {
                return getFeatureFlags().includes("KernelViewerRequestSourceFromFrontend");
            }
        },
        {
            key: "kernelViewerStreamingLogs",
            get: /**
   * Enables streaming logs to show on running kernel sessions in the kernel
   * viewer. b/150596662
   */ function get() {
                return getFeatureFlags().includes("KernelViewerStreamingLogs");
            }
        },
        {
            key: "datasetsMaterialDetail",
            get: /** Uses the new Material detail page. */ function get() {
                return getFeatureFlags().includes("DatasetsMaterialDetail");
            }
        },
        {
            key: "datasetsMaterialListComponent",
            get: /** Uses the new Material Dataset List component in existing contexts. */ function get() {
                return getFeatureFlags().includes("DatasetsMaterialListComponent");
            }
        },
        {
            key: "competitionDatasets",
            get: /** Datasets listing on Competition Page */ function get() {
                return getFeatureFlags().includes("CompetitionDatasets");
            }
        },
        {
            key: "competitionPrivateDatabundle",
            get: /** Enable viewing and uploading a new private databundle from the competition host page. */ function get() {
                return getFeatureFlags().includes("CompetitionPrivateDatabundle");
            }
        },
        {
            key: "discussionEmptyState",
            get: /** Whether to use KM EmptyState instead of styled Body in the DiscussionsTopicList component */ function get() {
                return getFeatureFlags().includes("DiscussionEmptyState");
            }
        },
        {
            key: "tpuCommitScheduling",
            get: /** Enables the TPU commit scheduling. */ function get() {
                return getFeatureFlags().includes("TPUCommitScheduling");
            }
        },
        {
            key: "commitScheduling",
            get: /** Enables commit scheduling. */ function get() {
                return getFeatureFlags().includes("CommitScheduling");
            }
        },
        {
            key: "allowForumAttachments",
            get: /**
   * Allows forum attachments. When disabled, a pop-up message is shown
   * when the attachment button is visible and clicked.
   */ function get() {
                return getFeatureFlags().includes("AllowForumAttachments");
            }
        },
        {
            key: "kagglePro",
            get: /** Kaggle Pro */ function get() {
                return getFeatureFlags().includes("KagglePro");
            }
        },
        {
            key: "kernelsSaveCellOutput",
            get: /** KernelsSaveCellOutput */ function get() {
                return getFeatureFlags().includes("KernelsSaveCellOutput");
            }
        },
        {
            key: "nextLoggedoutHomepage",
            get: /** Enable nextjs loggedout homepage */ function get() {
                return getFeatureFlags().includes("NextLoggedoutHomepage");
            }
        },
        {
            key: "kmLearnDetail",
            get: /** Enable KM Learn detail page. */ function get() {
                return getFeatureFlags().includes("KMLearnDetail");
            }
        },
        {
            key: "kmDataSourceSelector",
            get: /** Enables KM version of the data source selector in the kernels editor. */ function get() {
                return getFeatureFlags().includes("KMDataSourceSelector");
            }
        },
        {
            key: "kmEditorResponsive",
            get: /** Enable KM Editor responsive features. */ function get() {
                return getFeatureFlags().includes("KMEditorResponsive");
            }
        },
        {
            key: "kmEditorSidebar",
            get: /** Enable KM Editor sidebar. */ function get() {
                return getFeatureFlags().includes("KMEditorSidebar");
            }
        },
        {
            key: "frontendErrorReporting",
            get: /** Enable frontend JS error reporting to Stackdriver. */ function get() {
                return getFeatureFlags().includes("FrontendErrorReporting");
            }
        },
        {
            key: "frontendErrorReportingSampleRate",
            get: /**
   * A string containing a number between 0 and 1, which represents the rate
   * we should report unhandled frontend errors. ex: "0.1" means we should
   * report 10% of the errors we observe on frontend clients.
   * Requires EnableFrontendErrorReporting to have any functionality.
   */ function get() {
                return getFeatureFlagValue("FrontendErrorReportingSampleRate");
            }
        },
        {
            key: "frontendConsoleErrorReporting",
            get: /**
   * Enable frontend JS error reporting through console.error to Stackdriver.
   * Requires EnableFrontendErrorReporting to have any functionality.
   */ function get() {
                return getFeatureFlags().includes("FrontendConsoleErrorReporting");
            }
        },
        {
            key: "lowerDatasetHeaderImageMinRes",
            get: /** Lower the dataset header image minimum resolution */ function get() {
                return getFeatureFlags().includes("LowerDatasetHeaderImageMinRes");
            }
        },
        {
            key: "registrationNewsEmailSignupIsOptOut",
            get: function get() {
                return getFeatureFlags().includes("RegistrationNewsEmailSignupIsOptOut");
            }
        },
        {
            key: "phoneVerifyForComment",
            get: function get() {
                return getFeatureFlags().includes("PhoneVerifyForComments");
            }
        },
        {
            key: "phoneVerifyForNewTopic",
            get: function get() {
                return getFeatureFlags().includes("PhoneVerifyForNewTopic");
            }
        },
        {
            key: "filterForumImages",
            get: function get() {
                return getFeatureFlags().includes("FilterForumImages");
            }
        },
        {
            key: "emergencyAlertBanner",
            get: function get() {
                return getFeatureFlagValue("EmergencyAlertBanner");
            }
        },
        {
            key: "emergencyAlertBannerEnabled",
            get: function get() {
                return getFeatureFlags().includes("EmergencyAlertBannerEnabled");
            }
        },
        {
            key: "datasetsSharedWithYou",
            get: /**
   * Enables "Shared With You" under "Datasets" on the logged in user's Profile.
   */ function get() {
                return getFeatureFlags().includes("DatasetsSharedWithYou");
            }
        },
        {
            key: "clientRpcRateLimit",
            get: function get() {
                return getFeatureFlagValue("ClientRpcRateLimit");
            }
        },
        {
            key: "inClassToCommunityPages",
            get: function get() {
                return getFeatureFlags().includes("InClassToCommunityPages");
            }
        },
        {
            key: "discussionsSimpleSorts",
            get: function get() {
                return getFeatureFlags().includes("DiscussionsSimpleSorts");
            }
        },
        {
            key: "featuredCommunityCompetitions",
            get: /**
   * Allows admins to feature community competitions.
   */ function get() {
                return getFeatureFlagValue("FeaturedCommunityCompetitions");
            }
        },
        {
            key: "pinDatasetVersion",
            get: function get() {
                return getFeatureFlags().includes("PinDatasetVersion");
            }
        },
        {
            key: "addFeatureFlagsToPageLoadTag",
            get: function get() {
                return getFeatureFlagValue("AddFeatureFlagsToPageLoadTag");
            }
        },
        {
            key: "useStaticRoutingUrlFormat",
            get: function get() {
                return getFeatureFlags().includes("UseStaticRoutingUrlFormat");
            }
        },
        {
            key: "lihpNewModules",
            get: function get() {
                return getFeatureFlags().includes("LihpNewModules");
            }
        },
        {
            key: "lihpNextSteps",
            get: function get() {
                return getFeatureFlags().includes("LihpNextSteps");
            }
        },
        {
            key: "kmCompsSubmissionPage",
            get: function get() {
                return getFeatureFlags().includes("KmCompsSubmissionPage");
            }
        }
    ]);
    return FeatureFlags;
}();
export var featureFlags = new FeatureFlags();
// Allow feature flag inspection via console in dev.
if (process.env.NODE_ENV === "development") {
    window.featureFlags = featureFlags;
}
